import { contrastRatio, mixColors } from '@avrahami/contracts';
import { flyerPrice, splitTitle, textColorOn } from '../layout';
import {
  benefitIcon,
  bodyFont,
  cornerOf,
  displayFont,
  drawAmount,
  drawIcon,
  drawLines,
  drawLogo,
  drawPhoto,
  drawStruck,
  drawText,
  fillRect,
  fit,
  fitDescription,
  phoneOf,
  PHONE_ICON,
  readable,
  W,
  WHATSAPP_ICON,
  type Box,
  type Page,
} from '../kit';
import { clipBody, kickerOf, savingOf, tilesOf, WORDS, type Tile } from './shared';

/**
 * FRESH: a friendly bento. Rounded boxes on a pale page: the title on a box of the brand's colour
 * with a circle in its corner, the photo on a white box with a chip, three pastel tiles with the
 * product's figures, the price on white and the call to action on the accent.
 */

const RIGHT = 1040;
const LEFT = 40;
const GAP = 18;
const WHITE = '#ffffff';

/** A pastel of `color` and the deep shade of it that reads there. */
function pastel(color: string, ink: string): { ground: string; deep: string } {
  const ground = mixColors(color, WHITE, 0.8);
  const deep = mixColors(color, '#000000', 0.35);
  return { ground, deep: contrastRatio(deep, ground) >= 4 ? deep : ink };
}

export function drawFresh(page: Page) {
  const { ctx, flyer } = page;
  const { theme, storeDesign } = flyer;
  const ink = storeDesign.ink;
  const radius = cornerOf(storeDesign.corners, 34);
  const pageColor = mixColors(theme.brand, '#f6f6f2', 0.93);
  const muted = mixColors(ink, WHITE, 0.4);
  const brand = theme.brand;
  const onBrand = textColorOn(brand);
  const accent = theme.accent;
  const onAccent = textColorOn(accent);

  fillRect(ctx, { x: 0, y: 0, w: W, h: W }, pageColor);

  // The header: the logo, and a pill.
  drawLogo(
    page,
    page.images.logo,
    flyer.store.name,
    { x: 560, y: 46, w: RIGHT - 560, h: 84 },
    {
      align: 'right',
      color: ink,
    },
  );
  const saving = savingOf(flyer);
  const pill = saving !== null ? WORDS.sale : flyer.category;
  if (pill !== null) {
    const text = clipBody(page, pill, 700, 22, 360);
    ctx.font = bodyFont(page, 700, 22);
    const width = ctx.measureText(text).width + 52;
    fillRect(ctx, { x: 48, y: 64, w: width, h: 50 }, accent, 25);
    drawText(ctx, text, 48 + width / 2, 97, {
      font: bodyFont(page, 700, 22),
      color: onAccent,
      align: 'center',
    });
  }

  const tiles = tilesOf(flyer, 3);
  const heroBottom = tiles.length === 0 ? 813 : 587;

  // The title on the brand's box.
  const hero = { x: LEFT, y: 153, w: 576, h: heroBottom - 153 };
  fillRect(ctx, hero, brand, radius);
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(hero.x, hero.y, hero.w, hero.h, radius);
  ctx.clip();
  ctx.fillStyle = mixColors(brand, '#000000', 0.18);
  ctx.beginPath();
  ctx.arc(hero.x + 120, hero.y + 50, 170, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  const textRight = hero.x + hero.w - 30;
  const width = hero.w - 60;
  const [head, rest] = splitTitle(flyer.title);
  const highlight = readable(
    mixColors(accent, WHITE, 0.45),
    brand,
    [mixColors(accent, WHITE, 0.7), onBrand],
    3,
  );
  // Whole sentences only: a sentence cut in the middle is not drawn (fitDescription).
  const description = fitDescription(page, flyer.description, {
    maxWidth: width,
    maxLines: tiles.length === 0 ? 5 : 3,
    maxSize: 24,
    minSize: 20,
    weight: 400,
  });
  // The title shrinks until it, the kicker and the description fit in the box; a title that is
  // still too long leaves the description out.
  const room = hero.h - 34 - 70 - 40;
  let descriptionText = description;
  let headline = fit(page, head, 'display', {
    maxWidth: width,
    maxLines: 2,
    maxSize: 74,
    minSize: 74,
    weight: 800,
  });
  let second: ReturnType<typeof fit> | null = null;
  let titleHeight = 0;
  let descriptionHeight = 0;
  for (let size = 74; size >= 34; size -= 4) {
    headline = fit(page, head, 'display', {
      maxWidth: width,
      maxLines: 2,
      maxSize: size,
      minSize: size,
      weight: 800,
    });
    second =
      rest === ''
        ? null
        : fit(page, rest, 'display', {
            maxWidth: width,
            maxLines: 2,
            maxSize: size,
            minSize: size,
            weight: 800,
          });
    titleHeight =
      headline.lines.length * headline.size * 1.08 +
      (second === null ? 0 : second.lines.length * second.size * 1.08);
    descriptionHeight =
      descriptionText === null
        ? 0
        : descriptionText.lines.length * descriptionText.size * 1.55 + 18;
    if (titleHeight + descriptionHeight <= room) break;
  }
  if (titleHeight + descriptionHeight > room) {
    descriptionText = null;
    descriptionHeight = 0;
  }
  let y = Math.max(hero.y + 70, hero.y + hero.h - 34 - descriptionHeight - titleHeight - 44);
  drawText(ctx, clipBody(page, kickerOf(flyer), 700, 19, width), textRight, y + 20, {
    font: bodyFont(page, 700, 19),
    color: mixColors(onBrand, brand, 0.4),
    spacing: 1,
  });
  y += 40;
  y += drawLines(ctx, headline, {
    x: textRight,
    y,
    font: displayFont(page, headline.size),
    color: onBrand,
    lineHeight: 1.08,
  });
  if (second !== null) {
    y += drawLines(ctx, second, {
      x: textRight,
      y,
      font: displayFont(page, second.size),
      color: highlight,
      lineHeight: 1.08,
    });
  }
  if (descriptionText !== null) {
    drawLines(ctx, descriptionText, {
      x: textRight,
      y: y + 18,
      font: bodyFont(page, 400, descriptionText.size),
      color: mixColors(onBrand, brand, 0.15),
      lineHeight: 1.55,
    });
  }

  // The photo on white, with a chip and the maker's logo.
  const card = { x: 634, y: 153, w: RIGHT - 634, h: 660 };
  fillRect(ctx, card, WHITE, radius);
  drawPhoto(page, { x: card.x + 20, y: card.y + 80, w: card.w - 40, h: 480 }, radius / 2);
  const chipText = flyer.benefits[0]?.title ?? null;
  if (chipText !== null) {
    const chipGround = mixColors(accent, WHITE, 0.72);
    const text = clipBody(page, chipText, 700, 19, card.w - 80);
    ctx.font = bodyFont(page, 700, 19);
    const chipWidth = ctx.measureText(text).width + 36;
    fillRect(
      ctx,
      { x: card.x + card.w - 22 - chipWidth, y: card.y + 22, w: chipWidth, h: 44 },
      chipGround,
      22,
    );
    drawText(ctx, text, card.x + card.w - 22 - chipWidth / 2, card.y + 51, {
      font: bodyFont(page, 700, 19),
      color: readable(ink, chipGround, []),
      align: 'center',
    });
  }
  drawLogo(
    page,
    page.images.maker,
    null,
    { x: card.x + 26, y: card.y + card.h - 78, w: 150, h: 54 },
    {
      align: 'left',
      color: ink,
    },
  );

  // The pastel tiles.
  if (tiles.length > 0) {
    const tints = [
      pastel(accent, ink),
      pastel(mixColors(brand, accent, 0.5), ink),
      pastel(brand, ink),
    ];
    const widths = tiles.length === 3 ? [236, 152, 152] : tiles.length === 2 ? [279, 279] : [576];
    let right = hero.x + hero.w;
    tiles.forEach((tile, index) => {
      const w = widths[index] ?? 152;
      const box = { x: right - w, y: 605, w, h: 208 };
      const tint = tints[index] ?? tints[0];
      if (tint !== undefined) drawTile(page, tile, box, tint, radius);
      right -= w + GAP;
    });
  }

  // The price on white.
  const priceCard = { x: 463, y: 830, w: RIGHT - 463, h: 210 };
  fillRect(ctx, priceCard, WHITE, radius);
  const priceColor = readable(brand, WHITE, [ink], 4.5);
  const { price } = flyer;
  const priceRight = priceCard.x + priceCard.w - 28;
  if (price !== null) {
    drawText(ctx, saving === null ? WORDS.price : WORDS.salePrice, priceRight, 892, {
      font: bodyFont(page, 600, 20),
      color: muted,
    });
    const priceWidth = drawAmount(page, price.amountMinor, {
      x: priceRight,
      baseline: 986,
      size: 86,
      color: priceColor,
      maxWidth: 290,
    });
    if (saving !== null && price.compareAtMinor !== null) {
      // The old price struck through over the saving, in one chip beside the price.
      const chip = { x: priceRight - priceWidth - 30 - 168, y: 900, w: 168, h: 86 };
      if (chip.x >= priceCard.x + 20) {
        const ground = mixColors(brand, WHITE, 0.88);
        fillRect(ctx, chip, ground, Math.min(radius, 18));
        const middle = chip.x + chip.w / 2;
        drawStruck(page, price.compareAtMinor, {
          x: middle,
          baseline: chip.y + 34,
          font: bodyFont(page, 600, 20),
          color: readable(muted, ground, [ink], 3),
          strike: readable(accent, ground, [ink]),
          align: 'center',
        });
        const text = `${WORDS.saving} ${flyerPrice(saving)}`;
        const fitted = fit(page, text, 'body', {
          maxWidth: chip.w - 16,
          maxLines: 1,
          maxSize: 21,
          minSize: 15,
          weight: 800,
        });
        drawText(ctx, fitted.lines[0] ?? '', middle, chip.y + 70, {
          font: bodyFont(page, 800, fitted.size),
          color: readable(priceColor, ground, [ink], 4.5),
          align: 'center',
        });
      }
    }
  } else {
    drawText(ctx, WORDS.quote, priceRight, 930, {
      font: displayFont(page, 48),
      color: priceColor,
      maxWidth: priceCard.w - 56,
    });
    drawText(ctx, WORDS.noCommitment, priceRight, 972, {
      font: bodyFont(page, 600, 22),
      color: muted,
    });
  }

  // The call to action on the accent.
  const cta = { x: LEFT, y: 830, w: 407, h: 210 };
  fillRect(ctx, cta, accent, radius);
  const ctaRight = cta.x + cta.w - 28;
  const whatsapp = flyer.store.whatsapp !== null;
  // The words keep clear of the round mark at the box's left.
  const ctaWidth = cta.w - 28 - 170;
  for (const [text, baseline] of [
    [WORDS.cta, 898],
    [whatsapp ? WORDS.whatsapp : 'התקשרו אלינו', 938],
  ] as const) {
    const fitted = fit(page, text, 'body', {
      maxWidth: ctaWidth,
      maxLines: 1,
      maxSize: 28,
      minSize: 20,
      weight: 800,
    });
    drawText(ctx, fitted.lines[0] ?? '', ctaRight, baseline, {
      font: bodyFont(page, 800, fitted.size),
      color: onAccent,
    });
  }
  const phone = phoneOf(flyer);
  if (phone !== null) {
    drawText(ctx, clipBody(page, phone, 600, 22, 200), ctaRight, 984, {
      font: bodyFont(page, 600, 22),
      color: mixColors(onAccent, accent, 0.15),
      direction: 'ltr',
    });
  }
  ctx.fillStyle = WHITE;
  ctx.beginPath();
  ctx.arc(cta.x + 90, cta.y + cta.h / 2, 58, 0, Math.PI * 2);
  ctx.fill();
  drawIcon(
    ctx,
    whatsapp ? WHATSAPP_ICON : PHONE_ICON,
    cta.x + 90,
    cta.y + cta.h / 2,
    48,
    readable(accent, WHITE, [ink]),
    2,
  );
}

function drawTile(
  page: Page,
  tile: Tile,
  box: Box,
  tint: { ground: string; deep: string },
  radius: number,
) {
  const { ctx, flyer } = page;
  const ink = flyer.storeDesign.ink;
  fillRect(ctx, box, tint.ground, radius);
  const right = box.x + box.w - 22;
  if (tile.kind === 'FIGURE') {
    const { value, unit, label } = tile.figure;
    const unitWidth =
      unit === ''
        ? 0
        : drawText(ctx, unit, right, box.y + 84, {
            font: bodyFont(page, 800, 22),
            color: tint.deep,
            direction: 'ltr',
          });
    const big = fit(page, value, 'display', {
      maxWidth: box.w - 44 - unitWidth,
      maxLines: 1,
      maxSize: 66,
      minSize: 30,
      weight: 800,
    });
    drawText(ctx, big.lines[0] ?? '', right - unitWidth - (unit === '' ? 0 : 2), box.y + 84, {
      font: displayFont(page, big.size),
      color: tint.deep,
      direction: 'ltr',
    });
    const caption = fit(page, label, 'body', {
      maxWidth: box.w - 44,
      maxLines: 2,
      maxSize: 21,
      minSize: 17,
      weight: 600,
    });
    drawLines(ctx, caption, {
      x: right,
      y: box.y + 132,
      font: bodyFont(page, 600, caption.size),
      color: ink,
      lineHeight: 1.3,
    });
  } else {
    drawIcon(ctx, benefitIcon(tile.icon), right - 28, box.y + 58, 54, tint.deep, 2.2);
    const caption = fit(page, tile.title, 'body', {
      maxWidth: box.w - 44,
      maxLines: 3,
      maxSize: 21,
      minSize: 16,
      weight: 700,
    });
    drawLines(ctx, caption, {
      x: right,
      y: box.y + 112,
      font: bodyFont(page, 700, caption.size),
      color: ink,
      lineHeight: 1.3,
    });
  }
}
